import ReportOptionsModel, {
  IReportOptionsModelState,
  IReportOptions,
} from './ReportOptionsModel';
import { reportRouteManager } from '../main-navigation/RouteManager';
import { IScheduleGridOptions, ScheduleMode } from '../DataTypes';

type PostedScheduleMode = ScheduleMode | 'Mixed' | 'NewJob';

export interface ITimeEntriesOptions {
  showTemplates: boolean;
  showShiftPattern: boolean;
  showOccupations: boolean;
  showTimes: boolean;
  showUdfs: boolean;
  showReasons: boolean;
  showBreaks: boolean;
  exceptionsOnly: boolean;
  showComments: boolean;
}

export interface IPostedScheduleReportOptions extends IReportOptions {
  showAssignedEmployees: boolean;
  showShiftPatterns: boolean;
  timeEntriesOptions: ITimeEntriesOptions;
}

export interface IPostedScheduleReportOptionsModelState extends IReportOptionsModelState {
  mode: PostedScheduleMode;
  options: IPostedScheduleReportOptions;
}

export default class PostedScheduleReportOptionsModel extends ReportOptionsModel<IPostedScheduleReportOptionsModelState> {
  constructor(mode?: PostedScheduleMode) {
    super();

    this.state.mode = mode ?? 'Job';
    this.state.items.push(...[
      { name: 'showAssignedEmployees', title: 'Assigned Employee', subheader: 'Job Display Options', active: () => this.options?.['showAssignedEmployees'], onClick: this.toggleAssignedEmployee, visible: () => this.mode === 'Job' || this.mode === 'Mixed' },
      { name: 'showShiftPatterns', title: 'Job Shift Pattern', active: () => this.options?.['showShiftPatterns'], onClick: this.toggleShiftPatterns },
      { name: 'showTemplates', title: 'Shift Types', subheader: 'Shift Display Options', active: () => this.options?.timeEntriesOptions?.['showTemplates'], onClick: this.toggleTemplates },
      mode === 'NewJob' ? { name: 'showShiftPattern', title: 'Shift Pattern', active: () => this.options?.timeEntriesOptions?.['showShiftPattern'], onClick: this.toggleShiftPattern } : undefined as any,
      { name: 'showOccupations', title: 'Occupations', active: () => this.options?.timeEntriesOptions?.['showOccupations'], onClick: this.toggleOccupations, visible: () => this.mode === 'Employee' || this.mode === 'Mixed' },
      { name: 'showTimes', title: 'Times', active: () => this.options?.timeEntriesOptions?.['showTimes'], onClick: this.toggleTimes },
      mode === 'NewJob' ? { name: 'showUdfs', title: 'Udfs', active: () => this.options?.timeEntriesOptions?.['showUdfs'], onClick: this.toggleUdfs } : undefined as any,
      { name: 'showReasons', title: 'Reasons', active: () => this.options?.timeEntriesOptions?.['showReasons'], onClick: this.toggleReasons },
      { name: 'showBreaks', title: 'Breaks', active: () => this.options?.timeEntriesOptions?.['showBreaks'], onClick: this.toggleBreaks },
      { name: 'exceptionsOnly', title: 'Exceptions Only', active: () => this.options?.timeEntriesOptions?.['exceptionsOnly'], onClick: this.toggleExceptionsOnly },
      { name: 'showComments', title: 'Comments', active: () => this.options?.timeEntriesOptions?.['showComments'], onClick: this.toggleComments },
    ]);
  }

  get mode() {
    return this.state.mode;
  }

  get scheduleGridOptions() {
    return ({
      assignedEmployeeColumn: this.options.showAssignedEmployees,
      jobColumn: reportRouteManager.scheduleGridOptions?.jobColumn ?? true,
      jobCellGenerator: this.options.showShiftPatterns,
      shiftCellTemplate: this.options.timeEntriesOptions.showTemplates,
      shiftCellShiftPattern: this.options.timeEntriesOptions.showShiftPattern,
      shiftCellOccupations: this.options.timeEntriesOptions.showOccupations,
      shiftCellTimes: this.options.timeEntriesOptions.showTimes,
      shiftCellReasons: this.options.timeEntriesOptions.showReasons,
      shiftCellBreaks: this.options.timeEntriesOptions.showBreaks,
      shiftCellExceptionsOnly: this.options.timeEntriesOptions.exceptionsOnly,
    } as IScheduleGridOptions);
  }

  load() {
    super.load();
    if (reportRouteManager.scheduleGridOptions) {
      this.state.options.showAssignedEmployees = !!reportRouteManager.scheduleGridOptions.assignedEmployeeColumn;
      this.state.options.showShiftPatterns = !!reportRouteManager.scheduleGridOptions.jobCellGenerator;
      this.state.options.timeEntriesOptions = {
        showTemplates: !!reportRouteManager.scheduleGridOptions.shiftCellTemplate,
        showShiftPattern: !!reportRouteManager.scheduleGridOptions.shiftCellShiftPattern,
        showOccupations: !!reportRouteManager.scheduleGridOptions.shiftCellOccupations,
        showTimes: !!reportRouteManager.scheduleGridOptions.shiftCellTimes,
        showUdfs: reportRouteManager.options?.timeEntriesOptions?.showUdfs,
        showReasons: !!reportRouteManager.scheduleGridOptions.shiftCellReasons,
        showBreaks: !!reportRouteManager.scheduleGridOptions.shiftCellBreaks,
        exceptionsOnly: !!reportRouteManager.scheduleGridOptions.shiftCellExceptionsOnly,
        showComments: reportRouteManager.options?.timeEntriesOptions?.showComments ?? true,
      };
    } else {
      this.state.options.showAssignedEmployees = true;
      this.state.options.showShiftPatterns = true;
      this.state.options.timeEntriesOptions = this.getDefaultTimeEntriesOptions();
    }
  }

  getDefaultTimeEntriesOptions() {
    return ({
      showTemplates: true,
      showShiftPattern: false,
      showOccupations: true,
      showTimes: true,
      showUdfs: false,
      showReasons: true,
      showBreaks: false,
      exceptionsOnly: true,
      showComments: true,
    });
  }

  apply() {
    setTimeout(() => {
      reportRouteManager.navigateTo({ options: this.options, scheduleGridOptions: this.scheduleGridOptions });
    }, 0);
  }

  toggleAssignedEmployee = () => {
    this.state.options.showAssignedEmployees = !this.options.showAssignedEmployees;
    this.apply();
  };

  toggleShiftPatterns = () => {
    this.state.options.showShiftPatterns = !this.options.showShiftPatterns;
    this.apply();
  };

  toggleTemplates = () => {
    this.state.options.timeEntriesOptions.showTemplates = !this.options.timeEntriesOptions.showTemplates;
    this.apply();
  };

  toggleShiftPattern = () => {
    this.state.options.timeEntriesOptions.showShiftPattern = !this.options.timeEntriesOptions.showShiftPattern;
    this.apply();
  };

  toggleUdfs = () => {
    this.state.options.timeEntriesOptions.showUdfs = !this.options.timeEntriesOptions.showUdfs;
    this.apply();
  };

  toggleOccupations = () => {
    this.state.options.timeEntriesOptions.showOccupations = !this.options.timeEntriesOptions.showOccupations;
    this.apply();
  };

  toggleTimes = () => {
    this.state.options.timeEntriesOptions.showTimes = !this.options.timeEntriesOptions.showTimes;
    this.apply();
  };

  toggleReasons = () => {
    this.state.options.timeEntriesOptions.showReasons = !this.options.timeEntriesOptions.showReasons;
    if (!this.options.timeEntriesOptions.showReasons) {
      this.state.options.timeEntriesOptions.showBreaks = false;
    }
    this.apply();
  };

  toggleBreaks = () => {
    this.state.options.timeEntriesOptions.showBreaks = !this.options.timeEntriesOptions.showBreaks;
    if (this.options.timeEntriesOptions.showBreaks) {
      this.state.options.timeEntriesOptions.showReasons = true;
    }
    this.apply();
  };

  toggleExceptionsOnly = () => {
    this.state.options.timeEntriesOptions.exceptionsOnly = !this.options.timeEntriesOptions.exceptionsOnly;
    if (this.options.timeEntriesOptions.exceptionsOnly) {
      this.state.options.timeEntriesOptions.showReasons = true;
    }
    this.apply();
  };

  toggleComments = () => {
    this.state.options.timeEntriesOptions.showComments = !this.options.timeEntriesOptions.showComments;
    this.apply();
  };
}
